import type mapboxgl from 'mapbox-gl';
import polylabel from '@mapbox/polylabel';

export interface HintPlacement {
  x: number;
  y: number;
  width: number;
  arrowX: number;
  target: { x: number; y: number } | null;
}

const GAP = 16;
// The exported tail overlaps the card by 19px; its visible tip is 12px tall.
export const TARGET_GAP = 30;

/** Screen space left after the actual new-landing controls are laid out. */
function availableBounds(map: mapboxgl.Map) {
  const rect = map.getContainer().getBoundingClientRect();
  const bounds = {
    left: Math.max(0, rect.left) + GAP,
    right: Math.min(window.innerWidth, rect.right) - GAP,
    top: Math.max(0, rect.top) + GAP,
    bottom: Math.min(window.innerHeight, rect.bottom) - GAP,
  };
  const avoid = (
    selector: string,
    edge: 'top' | 'bottom' | 'left' | 'right',
  ) => {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      const box = element.getBoundingClientRect();
      if (
        !box.width ||
        !box.height ||
        getComputedStyle(element).visibility === 'hidden'
      )
        return;
      if (edge === 'top') bounds.top = Math.max(bounds.top, box.bottom + GAP);
      if (edge === 'bottom')
        bounds.bottom = Math.min(bounds.bottom, box.top - GAP);
      if (edge === 'left') bounds.left = Math.max(bounds.left, box.right + GAP);
      if (edge === 'right')
        bounds.right = Math.min(bounds.right, box.left - GAP);
    });
  };
  avoid(
    '.new-landing-page__header, .new-landing-page__explore-controls',
    'top',
  );
  avoid(
    '.new-landing-page__explore-bottom, .new-landing-page__desktop-explore-bottom, .jaakko-nav',
    'bottom',
  );
  avoid('.new-landing-page__desktop-explore-info-panel', 'left');
  avoid('.new-landing-page__desktop-explore-rail', 'right');
  return { bounds, rect };
}

/**
 * Identify viewport and control-bound changes so unchanged layouts can reuse
 * their placement without repeatedly querying and projecting building polygons.
 */
export function mapHintLayoutKey(map: mapboxgl.Map): string {
  const { bounds, rect } = availableBounds(map);
  return JSON.stringify([bounds, rect.left, rect.top, rect.width, rect.height]);
}

/** Keep the arrow on an ordinary, clickable footprint, never a courtyard or coin. */
export function placeMapHint(
  map: mapboxgl.Map,
  near: boolean,
  cardHeight: number,
): HintPlacement | null {
  const { bounds, rect } = availableBounds(map);
  const width = Math.min(304, bounds.right - bounds.left);
  const targetTop = bounds.top + cardHeight + TARGET_GAP;
  if (width < 240 || targetTop > bounds.bottom) return null;
  const ideal = {
    x: (bounds.left + bounds.right) / 2,
    y: Math.max(targetTop, (bounds.top + bounds.bottom) / 2 + 60),
  };
  let target: { x: number; y: number } | null = null;
  if (near) {
    if (!map.getLayer('building')) return null;
    const features = map.queryRenderedFeatures(
      [
        [bounds.left - rect.left, targetTop - rect.top],
        [bounds.right - rect.left, bounds.bottom - rect.top],
      ],
      { layers: ['building'] },
    );
    const markerLayers = [
      'interest-stage-coin',
      'interest-anchor-coin',
      'building-off-market-sell-icon',
      'building-off-market-sell-icon-selected',
      'building-off-market-sell-dot',
      'building-off-market-buy-icon',
    ].filter((id) => map.getLayer(id));
    let bestDistance = Infinity;
    for (const feature of features) {
      if (feature.id == null) continue;
      const polygons =
        feature.geometry.type === 'Polygon'
          ? [feature.geometry.coordinates]
          : feature.geometry.type === 'MultiPolygon'
            ? feature.geometry.coordinates
            : [];
      for (const polygon of polygons) {
        const rings = polygon.map((ring) =>
          ring.map(([lng, lat]) => {
            const point = map.project([lng, lat]);
            return [point.x, point.y];
          }),
        );
        if (
          !rings[0]?.length ||
          rings.some((ring) =>
            ring.some((point) => !point.every(Number.isFinite)),
          )
        )
          continue;
        // polylabel respects holes and keeps the target inside the footprint.
        const xs = rings[0].map((point) => point[0]);
        const ys = rings[0].map((point) => point[1]);
        if (
          Math.max(...xs) - Math.min(...xs) < 16 ||
          Math.max(...ys) - Math.min(...ys) < 16
        )
          continue;
        const point = polylabel(rings, 1);
        const x = point[0] + rect.left;
        const y = point[1] + rect.top;
        if (
          x < bounds.left + 32 ||
          x > bounds.right - 32 ||
          y < targetTop ||
          y > bounds.bottom - 14
        )
          continue;
        const distance = Math.hypot(x - ideal.x, y - ideal.y);
        if (distance >= bestDistance) continue;
        if (
          markerLayers.length &&
          map.queryRenderedFeatures(
            [
              [point[0] - 16, point[1] - 16],
              [point[0] + 16, point[1] + 16],
            ],
            { layers: markerLayers },
          ).length
        )
          continue;
        target = { x, y };
        bestDistance = distance;
      }
    }
    // No suitable building is loaded in the usable viewport. Wait for one.
    if (!target) return null;
  }
  const anchor = target ?? ideal;
  const x = Math.max(
    bounds.left,
    Math.min(anchor.x - width / 2, bounds.right - width),
  );
  return { x, y: anchor.y - TARGET_GAP, width, arrowX: anchor.x - x, target };
}
